import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["disabled", "aria-expanded"]
const _hoisted_2 = { class: "min-w-0 flex-1 truncate text-left" }
const _hoisted_3 = ["disabled", "onClick", "onMouseenter"]
const _hoisted_4 = { class: "min-w-0 flex-1 truncate" }
const _hoisted_5 = {
  key: 0,
  class: "h-4 w-4 shrink-0 text-blue-700",
  fill: "none",
  viewBox: "0 0 24 24",
  stroke: "currentColor",
  "stroke-width": "3",
  "aria-hidden": "true"
}

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

type SelectValue = string | number | boolean | null | undefined;
type SelectOption =
  | SelectValue
  | {
      label?: string | number;
      name?: string | number;
      value?: SelectValue;
      id?: SelectValue;
      disabled?: boolean;
    };


export default /*@__PURE__*/_defineComponent({
  __name: 'AppSelect',
  props: {
    modelValue: { type: [String, Number, Boolean, null], default: '' },
    options: {},
    placeholder: { default: 'Pilih' },
    disabled: { type: Boolean, default: false },
    buttonClass: { default: '' }
  },
  emits: ["update:modelValue", "change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const rootRef = ref<HTMLElement | null>(null);
const buttonRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const activeIndex = ref(-1);
const menuStyle = ref<Record<string, string>>({});

const normalizedOptions = computed(() =>
  props.options.map((option) => {
    if (option && typeof option === 'object') {
      const value = option.value ?? option.id ?? '';
      return {
        value,
        label: String(option.label ?? option.name ?? value),
        disabled: Boolean(option.disabled),
      };
    }

    return {
      value: option,
      label: String(option ?? ''),
      disabled: false,
    };
  })
);

const selectedLabel = computed(() => {
  const selected = normalizedOptions.value.find((option) => isSelected(option.value));
  return selected?.label || '';
});

const isSelected = (value: SelectValue) => Object.is(value, props.modelValue);

const updateMenuPosition = () => {
  const button = buttonRef.value;
  if (!button) return;

  const rect = button.getBoundingClientRect();
  const menuHeight = Math.min(256, Math.max(40, normalizedOptions.value.length * 38));
  const gap = 6;
  const hasBottomSpace = rect.bottom + gap + menuHeight <= window.innerHeight - 12;
  const top = hasBottomSpace ? rect.bottom + gap : Math.max(12, rect.top - gap - menuHeight);

  menuStyle.value = {
    left: `${rect.left}px`,
    top: `${top}px`,
    width: `${rect.width}px`,
  };
};

const open = async () => {
  if (props.disabled) return;
  isOpen.value = true;
  activeIndex.value = Math.max(
    0,
    normalizedOptions.value.findIndex((option) => isSelected(option.value))
  );
  await nextTick();
  updateMenuPosition();
};

const close = () => {
  isOpen.value = false;
};

const toggle = () => {
  if (isOpen.value) close();
  else open();
};

const selectOption = (option: { value: SelectValue; disabled: boolean }) => {
  if (option.disabled) return;
  emit('update:modelValue', option.value);
  emit('change', option.value);
  close();
};

const moveActive = (direction: 1 | -1) => {
  const enabled = normalizedOptions.value
    .map((option, index) => ({ option, index }))
    .filter(({ option }) => !option.disabled);
  if (!enabled.length) return;

  const currentPosition = enabled.findIndex(({ index }) => index === activeIndex.value);
  const nextPosition = currentPosition === -1
    ? 0
    : (currentPosition + direction + enabled.length) % enabled.length;
  activeIndex.value = enabled[nextPosition].index;
};

const onButtonKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    if (!isOpen.value) open();
    else moveActive(event.key === 'ArrowDown' ? 1 : -1);
    return;
  }

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    if (!isOpen.value) open();
    else {
      const option = normalizedOptions.value[activeIndex.value];
      if (option) selectOption(option);
    }
    return;
  }

  if (event.key === 'Escape') close();
};

const onDocumentPointerDown = (event: PointerEvent) => {
  const target = event.target as Node;
  if (rootRef.value?.contains(target) || menuRef.value?.contains(target)) return;
  close();
};

watch(isOpen, (openState) => {
  if (openState) {
    document.addEventListener('pointerdown', onDocumentPointerDown);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
  } else {
    document.removeEventListener('pointerdown', onDocumentPointerDown);
    window.removeEventListener('resize', updateMenuPosition);
    window.removeEventListener('scroll', updateMenuPosition, true);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown);
  window.removeEventListener('resize', updateMenuPosition);
  window.removeEventListener('scroll', updateMenuPosition, true);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: "relative"
  }, [
    _createElementVNode("button", {
      ref_key: "buttonRef",
      ref: buttonRef,
      type: "button",
      disabled: __props.disabled,
      "aria-expanded": isOpen.value,
      class: _normalizeClass(["iom-control-border inline-flex min-h-[38px] w-full items-center justify-between gap-3 rounded-lg border bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#8c8c94]/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400", __props.buttonClass]),
      onClick: toggle,
      onKeydown: onButtonKeydown
    }, [
      _createElementVNode("span", _hoisted_2, _toDisplayString(selectedLabel.value || __props.placeholder), 1),
      (_openBlock(), _createElementBlock("svg", {
        class: _normalizeClass(["h-5 w-5 shrink-0 text-slate-700 transition-transform", { 'rotate-180': isOpen.value }]),
        fill: "none",
        viewBox: "0 0 24 24",
        stroke: "currentColor",
        "stroke-width": "3.25",
        "aria-hidden": "true"
      }, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("path", {
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
          d: "m6 9 6 6 6-6"
        }, null, -1)
      ]))], 2))
    ], 42, _hoisted_1),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "menuRef",
            ref: menuRef,
            class: "fixed z-[1200] max-h-64 overflow-y-auto rounded-lg border border-[#8c8c94] bg-white shadow-xl ring-1 ring-black/5",
            style: _normalizeStyle(menuStyle.value)
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(normalizedOptions.value, (option, index) => {
              return (_openBlock(), _createElementBlock("button", {
                key: `${String(option.value)}-${index}`,
                type: "button",
                disabled: option.disabled,
                class: _normalizeClass(["flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm transition disabled:cursor-not-allowed disabled:text-slate-300", [
            isSelected(option.value) ? 'bg-blue-50 font-semibold text-blue-800' : 'font-medium text-slate-700 hover:bg-slate-50',
            activeIndex.value === index && !option.disabled ? 'bg-slate-100' : '',
          ]]),
                onClick: ($event: any) => (selectOption(option)),
                onMouseenter: ($event: any) => (activeIndex.value = index)
              }, [
                _createElementVNode("span", _hoisted_4, _toDisplayString(option.label), 1),
                (isSelected(option.value))
                  ? (_openBlock(), _createElementBlock("svg", _hoisted_5, [...(_cache[1] || (_cache[1] = [
                      _createElementVNode("path", {
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        d: "m5 13 4 4L19 7"
                      }, null, -1)
                    ]))]))
                  : _createCommentVNode("", true)
              ], 42, _hoisted_3))
            }), 128))
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ], 512))
}
}

})